import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  header: {
    marginBottom: '16px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
    margin: 0,
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    margin: '4px 0 0 0',
  },
  totalBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    padding: '12px 16px',
    background: '#f9fafb',
    borderRadius: '6px',
    border: '1px solid #f3f4f6',
  },
  totalLabel: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: 500,
  },
  totalValue: {
    fontSize: '24px',
    fontWeight: 700,
  },
  totalArrow: {
    fontSize: '20px',
    fontWeight: 700,
  },
  periodLabel: {
    fontSize: '11px',
    color: '#9ca3af',
    marginLeft: 'auto',
  },
  chartRow: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  mainChart: {
    flex: '2 1 400px',
    minWidth: '400px',
  },
  sidePanel: {
    flex: '1 1 260px',
    minWidth: '260px',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  sectionTitle: {
    fontSize: '13px',
    fontWeight: 600,
    color: '#374151',
    marginBottom: '8px',
  },
  container: {
    overflowX: 'auto',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '12px',
    marginBottom: '12px',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '12px',
    fontWeight: 500,
  },
  legendSwatch: {
    width: '14px',
    height: '14px',
    borderRadius: '2px',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

export default function PerformanceAttributionChart({ data }) {
  const waterfallSvgRef = useRef(null);
  const stateSvgRef = useRef(null);
  const fieldSvgRef = useRef(null);
  const waterfallContainerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);

  // Waterfall chart
  useEffect(() => {
    if (!data || !data.attributions || data.attributions.length === 0) return;

    const svg = d3.select(waterfallSvgRef.current);
    svg.selectAll('*').remove();

    const containerWidth = waterfallContainerRef.current
      ? waterfallContainerRef.current.clientWidth
      : 500;
    const width = Math.max(400, containerWidth);
    const height = 320;
    const margin = { top: 30, right: 20, bottom: 60, left: 60 };
    const innerW = width - margin.left - margin.right;
    const innerH = height - margin.top - margin.bottom;

    svg.attr('width', width).attr('height', height);

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    // Build waterfall data: start at 0, each factor adds its contribution, final bar = total
    const items = [];
    let cumulative = 0;

    data.attributions.forEach((attr) => {
      items.push({
        label: attr.factor,
        contribution: attr.contribution,
        percent: attr.percent,
        color: attr.color,
        start: cumulative,
        end: cumulative + attr.contribution,
        type: 'factor',
      });
      cumulative += attr.contribution;
    });

    // Total bar
    items.push({
      label: 'Total',
      contribution: data.totalChange,
      percent: 100,
      color: '#1f2937',
      start: 0,
      end: data.totalChange,
      type: 'total',
    });

    // Determine y domain covering all bar positions
    const allValues = [];
    items.forEach((d) => {
      allValues.push(d.start);
      allValues.push(d.end);
    });
    allValues.push(0);
    const yMin = Math.min(...allValues);
    const yMax = Math.max(...allValues);
    const yPadding = (yMax - yMin) * 0.15 || 0.5;

    // Scales
    const x = d3.scaleBand()
      .domain(items.map((d) => d.label))
      .range([0, innerW])
      .padding(0.35);

    const y = d3.scaleLinear()
      .domain([yMin - yPadding, yMax + yPadding])
      .nice()
      .range([innerH, 0]);

    // Grid lines
    g.selectAll('.grid-line')
      .data(y.ticks())
      .enter()
      .append('line')
      .attr('class', 'grid-line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', (d) => y(d))
      .attr('y2', (d) => y(d))
      .attr('stroke', '#f3f4f6')
      .attr('stroke-width', 1);

    // Zero line
    g.append('line')
      .attr('x1', 0)
      .attr('x2', innerW)
      .attr('y1', y(0))
      .attr('y2', y(0))
      .attr('stroke', '#d1d5db')
      .attr('stroke-width', 1);

    // X axis
    g.append('g')
      .attr('transform', `translate(0, ${innerH})`)
      .call(d3.axisBottom(x).tickSize(0))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280')
      .attr('transform', 'rotate(-25)')
      .attr('text-anchor', 'end')
      .attr('dx', '-0.3em')
      .attr('dy', '0.6em');

    // Y axis
    g.append('g')
      .call(d3.axisLeft(y).ticks(6).tickSize(0).tickFormat((v) => `${v}%`))
      .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
      .selectAll('text')
      .attr('font-size', '10px')
      .attr('fill', '#6b7280');

    g.selectAll('.tick line').attr('stroke', '#e5e7eb');

    const barWidth = x.bandwidth();

    // Draw bars and connectors
    items.forEach((d, i) => {
      const barX = x(d.label);
      const barTop = Math.min(y(d.start), y(d.end));
      const barBottom = Math.max(y(d.start), y(d.end));
      const barHeight = Math.max(1, barBottom - barTop);

      // Connector line from previous factor end to this factor start
      if (i > 0 && d.type !== 'total') {
        const prevItem = items[i - 1];
        const prevBarX = x(prevItem.label) + barWidth;
        const connectorY = y(prevItem.end);

        g.append('line')
          .attr('x1', prevBarX)
          .attr('x2', barX)
          .attr('y1', connectorY)
          .attr('y2', connectorY)
          .attr('stroke', '#d1d5db')
          .attr('stroke-width', 1)
          .attr('stroke-dasharray', '3,2');
      }

      // Bar
      g.append('rect')
        .attr('x', barX)
        .attr('y', barTop)
        .attr('width', barWidth)
        .attr('height', barHeight)
        .attr('fill', d.color)
        .attr('rx', 2);

      // Value label above/below bar
      const isPositive = d.contribution >= 0;
      const labelY = isPositive ? barTop - 6 : barBottom + 14;
      const sign = d.type === 'total' ? '' : (d.contribution >= 0 ? '+' : '');

      g.append('text')
        .attr('x', barX + barWidth / 2)
        .attr('y', labelY)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', d.color)
        .text(`${sign}${d.contribution.toFixed(2)}%`);

      // Invisible hover target
      g.append('rect')
        .attr('x', barX)
        .attr('y', 0)
        .attr('width', barWidth)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            factor: d.label,
            contribution: d.contribution,
            percent: d.percent,
            type: d.type,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // By-state horizontal bar chart
  useEffect(() => {
    if (!data || !data.byState || data.byState.length === 0) return;

    const svg = d3.select(stateSvgRef.current);
    svg.selectAll('*').remove();

    const barH = 20;
    const gap = 4;
    const margin = { top: 10, right: 50, bottom: 10, left: 30 };
    const width = 300;
    const innerW = width - margin.left - margin.right;
    const totalHeight = margin.top + margin.bottom + data.byState.length * (barH + gap);

    svg.attr('viewBox', `0 0 ${width} ${totalHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    const maxVal = d3.max(data.byState, (d) => Math.abs(d.contribution)) || 1;

    const x = d3.scaleLinear()
      .domain([0, maxVal * 1.15])
      .range([0, innerW]);

    // Sort by contribution descending
    const sorted = [...data.byState].sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

    sorted.forEach((d, i) => {
      const barY = i * (barH + gap);
      const barWidth = x(Math.abs(d.contribution));
      const isPositive = d.contribution >= 0;

      // State label
      g.append('text')
        .attr('x', -4)
        .attr('y', barY + barH / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '11px')
        .attr('font-weight', 500)
        .attr('fill', '#374151')
        .text(d.state);

      // Bar
      g.append('rect')
        .attr('x', 0)
        .attr('y', barY)
        .attr('width', Math.max(0, barWidth))
        .attr('height', barH)
        .attr('fill', isPositive ? '#2563eb' : '#dc2626')
        .attr('opacity', 0.75)
        .attr('rx', 2)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            factor: d.state,
            contribution: d.contribution,
            percent: null,
            type: 'state',
            addresses: d.addresses,
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });

      // Value label
      g.append('text')
        .attr('x', barWidth + 4)
        .attr('y', barY + barH / 2)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', isPositive ? '#2563eb' : '#dc2626')
        .text(`${d.contribution >= 0 ? '+' : ''}${d.contribution.toFixed(2)}%`);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  // By-field horizontal bar chart
  useEffect(() => {
    if (!data || !data.byField || data.byField.length === 0) return;

    const svg = d3.select(fieldSvgRef.current);
    svg.selectAll('*').remove();

    const fieldColors = {
      pest365: '#2563eb',
      pest365Plus: '#8b5cf6',
      oneTime: '#f59e0b',
      initialServiceVisit: '#16a34a',
      mosquitoTickControl: '#dc2626',
    };

    const barH = 22;
    const gap = 6;
    const margin = { top: 10, right: 50, bottom: 10, left: 110 };
    const width = 300;
    const innerW = width - margin.left - margin.right;
    const totalHeight = margin.top + margin.bottom + data.byField.length * (barH + gap);

    svg.attr('viewBox', `0 0 ${width} ${totalHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('width', '100%')
      .style('height', 'auto');

    const g = svg.append('g').attr('transform', `translate(${margin.left}, ${margin.top})`);

    const maxVal = d3.max(data.byField, (d) => Math.abs(d.contribution)) || 1;

    const x = d3.scaleLinear()
      .domain([0, maxVal * 1.15])
      .range([0, innerW]);

    const sorted = [...data.byField].sort((a, b) => Math.abs(b.contribution) - Math.abs(a.contribution));

    const fieldLabels = {
      pest365: 'Pest365',
      pest365Plus: 'Pest365+',
      oneTime: 'One-Time',
      initialServiceVisit: 'Init. Visit',
      mosquitoTickControl: 'Mosquito/Tick',
    };

    sorted.forEach((d, i) => {
      const barY = i * (barH + gap);
      const barWidth = x(Math.abs(d.contribution));
      const isPositive = d.contribution >= 0;
      const color = fieldColors[d.field] || '#6b7280';

      // Field label
      g.append('text')
        .attr('x', -6)
        .attr('y', barY + barH / 2)
        .attr('dy', '0.35em')
        .attr('text-anchor', 'end')
        .attr('font-size', '11px')
        .attr('font-weight', 500)
        .attr('fill', '#374151')
        .text(fieldLabels[d.field] || d.field);

      // Bar
      g.append('rect')
        .attr('x', 0)
        .attr('y', barY)
        .attr('width', Math.max(0, barWidth))
        .attr('height', barH)
        .attr('fill', color)
        .attr('opacity', 0.75)
        .attr('rx', 2)
        .style('cursor', 'pointer')
        .on('mousemove', (event) => {
          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            factor: fieldLabels[d.field] || d.field,
            contribution: d.contribution,
            percent: null,
            type: 'field',
          });
        })
        .on('mouseleave', () => {
          setTooltip(null);
        });

      // Value label
      g.append('text')
        .attr('x', barWidth + 4)
        .attr('y', barY + barH / 2)
        .attr('dy', '0.35em')
        .attr('font-size', '10px')
        .attr('font-weight', 600)
        .attr('fill', color)
        .text(`${d.contribution >= 0 ? '+' : ''}${d.contribution.toFixed(2)}%`);
    });

    return () => {
      svg.selectAll('*').remove();
    };
  }, [data]);

  if (!data) {
    return (
      <div style={styles.card}>
        <div style={styles.header}>
          <div style={styles.title}>Performance Attribution</div>
          <div style={styles.subtitle}>Decomposing price changes by contributing factors</div>
        </div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          No attribution data available
        </div>
      </div>
    );
  }

  const totalChange = data.totalChange || 0;
  const isPositive = totalChange >= 0;
  const arrowChar = isPositive ? '\u2191' : '\u2193';
  const changeColor = totalChange > 0 ? '#16a34a' : totalChange < 0 ? '#dc2626' : '#6b7280';

  const formatPeriod = (period) => {
    if (!period || !period.from || !period.to) return '';
    const fmt = (dateStr) => {
      const d = new Date(dateStr);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${months[d.getUTCMonth()]} ${d.getUTCDate()}, ${d.getUTCFullYear()}`;
    };
    return `${fmt(period.from)} - ${fmt(period.to)}`;
  };

  return (
    <div style={styles.card}>
      <div style={styles.header}>
        <div style={styles.title}>Performance Attribution</div>
        <div style={styles.subtitle}>Decomposing price changes by contributing factors</div>
      </div>

      {/* Total change banner */}
      <div style={styles.totalBanner}>
        <span style={styles.totalLabel}>Total Price Change</span>
        <span style={{ ...styles.totalArrow, color: changeColor }}>{arrowChar}</span>
        <span style={{ ...styles.totalValue, color: changeColor }}>
          {totalChange >= 0 ? '+' : ''}{totalChange.toFixed(2)}%
        </span>
        {data.period && (
          <span style={styles.periodLabel}>{formatPeriod(data.period)}</span>
        )}
      </div>

      {/* Legend */}
      {data.attributions && data.attributions.length > 0 && (
        <div style={styles.legend}>
          {data.attributions.map((attr) => (
            <div key={attr.factor} style={styles.legendItem}>
              <span style={{ ...styles.legendSwatch, background: attr.color }} />
              <span style={{ color: '#374151' }}>{attr.factor}</span>
            </div>
          ))}
        </div>
      )}

      <div style={styles.chartRow}>
        {/* Main waterfall chart */}
        <div style={styles.mainChart}>
          <div style={styles.sectionTitle}>Attribution Waterfall</div>
          <div style={styles.container} ref={waterfallContainerRef}>
            <svg ref={waterfallSvgRef} />
          </div>
        </div>

        {/* Side panel */}
        <div style={styles.sidePanel}>
          {/* By-state breakdown */}
          {data.byState && data.byState.length > 0 && (
            <div>
              <div style={styles.sectionTitle}>By State</div>
              <div style={styles.container}>
                <svg ref={stateSvgRef} />
              </div>
            </div>
          )}

          {/* By-field breakdown */}
          {data.byField && data.byField.length > 0 && (
            <div>
              <div style={styles.sectionTitle}>By Price Field</div>
              <div style={styles.container}>
                <svg ref={fieldSvgRef} />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {tooltip.factor}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Contribution</span>
            <span
              style={{
                fontWeight: 600,
                color: tooltip.contribution >= 0 ? '#16a34a' : '#dc2626',
              }}
            >
              {tooltip.contribution >= 0 ? '+' : ''}{tooltip.contribution.toFixed(2)}%
            </span>
          </div>
          {tooltip.percent != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
              <span style={{ color: '#6b7280' }}>Share of Total</span>
              <span style={{ fontWeight: 600 }}>{tooltip.percent.toFixed(1)}%</span>
            </div>
          )}
          {tooltip.type === 'state' && tooltip.addresses != null && (
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
              <span style={{ color: '#6b7280' }}>Addresses</span>
              <span style={{ fontWeight: 600 }}>{tooltip.addresses}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
